import auth from '@common/auth';
import { InvestorRolesModel } from '@common/models/InvestorRoles';
import session from '@common/session';
import create from 'zustand';
import { ROUTES } from '../../config';

interface SignInViewModelProps {
  mfaDeliveryDestination?: string;
}
interface SignInViewModelActions {
  signIn: (
    email: string,
    password: string,
  ) => Promise<{
    success: boolean;
    challenge?: boolean;
    isConfirmChallenge?: boolean;
    error?: string;
    signInRoute?: string;
  }>;
  requestPasswordReset: (email: string) => Promise<{ success: boolean; error?: string }>;
  verifyPasswordResetCode: (
    email: string,
    code: string,
    newPassword: string,
  ) => Promise<{ success: boolean; error?: string }>;
  verifySignInCode: (
    code: string,
    confirmSignUp?: boolean,
    email?: string,
  ) => Promise<{ success: boolean; error?: string; signInRoute?: string; confirmedSignUp?: boolean }>;
}

export const useSignInViewModel = create<SignInViewModelProps & SignInViewModelActions>((set, get) => ({
  requestPasswordReset: async (email: string) => {
    try {
      await auth.forgotPassword(email.toLowerCase());
      return { success: true };
    } catch (err) {
      return {
        error: 'Password reset request failed. Are you sure you entered in the correct email address?',
        success: false,
      };
    }
  },
  signIn: async (email: string, password: string) => {
    const { getMyInvestorId } = InvestorRolesModel.getState();
    const searchParams = new URLSearchParams(window.location.search);
    const redirectParam = searchParams.get('redirect');
    const redirectAsParam = redirectParam ? '?redirect=' + encodeURIComponent(redirectParam) : '';
    const redirectUrl = redirectParam ? decodeURIComponent(redirectParam) : ROUTES.dashboard;
    const redirectToCitizenshipUrl = ROUTES.createCitizenship + redirectAsParam;
    const normalizedEmail = email.toLowerCase();
    try {
      await auth.signOut();
      await session.end();

      const signInResult = await auth.signIn({
        password: password,
        username: normalizedEmail,
      });

      if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
        const mfaDeliveryMedium = signInResult?.nextStep?.codeDeliveryDetails?.deliveryMedium || '';
        const phoneDestination = ['SMS', 'PHONE'].includes(mfaDeliveryMedium)
          ? signInResult?.nextStep?.codeDeliveryDetails?.destination
          : undefined;
        set({ mfaDeliveryDestination: phoneDestination });
        return { challenge: true, signInRoute: redirectToCitizenshipUrl, success: true };
      } else if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        // If the account requires confirmation before sign in, send a confirmation code.
        const sendCodeResult = await auth.resendCode(normalizedEmail);
        if (sendCodeResult.destination) {
          set({ mfaDeliveryDestination: sendCodeResult.destination });
        }
        return { challenge: true, isConfirmChallenge: true, signInRoute: redirectToCitizenshipUrl, success: true };
      } else if (signInResult.nextStep?.signInStep === 'DONE') {
        const investorId = await getMyInvestorId();
        if (investorId) {
          await session.start(investorId);
          return { challenge: false, signInRoute: redirectUrl, success: true };
        } else {
          return { challenge: false, signInRoute: redirectToCitizenshipUrl, success: true };
        }
      } else {
        return { error: 'Unexpected sign in state reached', success: false };
      }
    } catch (err: any) {
      // don't log anything here, if we want to capture errors here do it on the BE
      // or do multiple try/catch blogs to isolate auth.signin which throws
      return { error: 'Incorrect email or password', success: false };
    }
  },
  verifyPasswordResetCode: async (email: string, code: string, newPassword: string) => {
    try {
      await auth.forgotPasswordSubmit(email, code, newPassword);
      return { success: true };
    } catch (err: any) {
      return {
        error: auth.ERROR_CODES[err.code] || auth.ERROR_CODES._DEFAULT,
        success: false,
      };
    }
  },
  verifySignInCode: async (code: string, confirmSignUp = false, email?: string) => {
    const { getMyInvestorId } = InvestorRolesModel.getState();
    const searchParams = new URLSearchParams(window.location.search);
    const redirectParam = searchParams.get('redirect');
    const redirectAsParam = redirectParam ? '?redirect=' + redirectParam : '';
    const redirectUrl = redirectParam ? decodeURIComponent(redirectParam) : ROUTES.dashboard;
    const redirectToCitizenshipUrl = ROUTES.createCitizenship + redirectAsParam;

    try {
      if (confirmSignUp) {
        const result = await auth.confirmSignUp({ code, email: email as string });
        if (!result) {
          throw new Error('Failed to confirm sign up code.');
        }
        return { confirmedSignUp: true, signInRoute: ROUTES.signin, success: true };
      } else {
        const result = await auth.confirmSignIn({ code });
        if (result.nextStep.signInStep !== 'DONE') {
          throw new Error(`Failed to confirm sign in code. Next step is ${result.nextStep.signInStep}`);
        }
      }
      const investorId = await getMyInvestorId();
      if (investorId) {
        await session.start(investorId);
        return { signInRoute: redirectUrl, success: true };
      }
      return { signInRoute: redirectToCitizenshipUrl, success: true };
    } catch (err: any) {
      let errorMessage;
      switch (err.code) {
        case 'NotAuthorizedException':
          errorMessage = 'Your credentials did not match. Please try again.';
          break;
        case 'UserNotConfirmedException':
        case 'PasswordResetRequiredException':
        case 'UserNotFoundException':
        default:
          errorMessage = 'Code did not match. Please try again.';
      }
      return {
        error: errorMessage,
        success: false,
      };
    }
  },
}));
